import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { Plus, Trash2, RefreshCw, Link as LinkIcon, ToggleLeft, ToggleRight, Clock } from 'lucide-react';
import { formatDate } from '../lib/utils';

const GoogleSheetSync = () => {
  const [syncs, setSyncs] = useState([]);
  const [newSheetUrl, setNewSheetUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const user = authService.getCurrentUser();

  useEffect(() => {
    if (!['admin', 'manager'].includes(user?.role)) {
      return;
    }
    fetchSyncs();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchSyncs, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const fetchSyncs = async () => {
    try {
      const response = await api.getGoogleSheetSyncs();
      setSyncs(response.data);
    } catch (error) {
      console.error('Failed to fetch syncs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSync = async (e) => {
    e.preventDefault();
    if (!newSheetUrl.trim()) {
      toast.error('Please enter a Google Sheets URL');
      return;
    }

    if (!newSheetUrl.includes('docs.google.com/spreadsheets')) {
      toast.error('Please enter a valid Google Sheets URL');
      return;
    }

    try {
      await api.createGoogleSheetSync(newSheetUrl);
      toast.success('Google Sheet sync added successfully');
      setNewSheetUrl('');
      fetchSyncs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add Google Sheet sync');
    }
  };

  const handleSync = async (syncId) => {
    setSyncing(syncId);
    try {
      const response = await api.syncGoogleSheet(syncId);
      toast.success(response.data.message);
      fetchSyncs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(null);
    }
  };

  const handleToggle = async (syncId) => {
    try {
      const response = await api.toggleGoogleSheetSync(syncId);
      toast.success(response.data.message);
      fetchSyncs();
    } catch (error) {
      toast.error('Failed to toggle sync');
    }
  };

  const handleDelete = async (syncId) => {
    if (!window.confirm('Are you sure you want to delete this sync configuration?')) {
      return;
    }

    try {
      await api.deleteGoogleSheetSync(syncId);
      toast.success('Sync configuration deleted');
      fetchSyncs();
    } catch (error) {
      toast.error('Failed to delete sync configuration');
    }
  };

  if (!['admin', 'manager'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <LinkIcon size={48} className="mx-auto text-slate-300 mb-4" />
        <p className="text-slate-600 font-body">You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600 font-body">Loading sync configurations...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold font-headings uppercase tracking-tight text-slate-900">
          Google Sheet Auto-Sync
        </h1>
        <p className="text-slate-600 font-body mt-1">
          Automatically import leads from Google Sheets - new rows are synced to avoid duplicates
        </p>
      </div>

      {/* Add New Sync */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold font-headings text-slate-900 mb-4">Add Google Sheet</h2>
        <form onSubmit={handleAddSync} className="space-y-4">
          <div>
            <input
              type="url"
              data-testid="new-sheet-url-input"
              value={newSheetUrl}
              onChange={(e) => setNewSheetUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/..."
              className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
            />
            <p className="text-xs text-slate-500 font-body mt-2">
              ⚠️ Make sure the sheet is publicly accessible or shared with "Anyone with the link can view"
            </p>
          </div>
          <button
            type="submit"
            data-testid="add-sheet-sync-button"
            className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-all shadow-sm font-body"
          >
            <Plus size={18} />
            <span>Add Sheet</span>
          </button>
        </form>
      </div>

      {/* Sync Configurations */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-lg font-semibold font-headings text-slate-900">
            Active Syncs ({syncs.length})
          </h2>
        </div>

        {syncs.length === 0 ? (
          <div className="p-12 text-center">
            <LinkIcon size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600 font-body">No Google Sheet syncs configured</p>
            <p className="text-slate-500 font-body text-sm mt-2">
              Add a Google Sheet URL above to start auto-syncing leads
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {syncs.map((sync) => (
              <div
                key={sync.id}
                data-testid={`sync-${sync.id}`}
                className="p-6 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <LinkIcon size={20} className={sync.is_active ? 'text-green-600' : 'text-slate-400'} />
                      <h3 className="font-semibold text-slate-900 font-body truncate">
                        Google Sheet Sync
                      </h3>
                      {sync.is_active ? (
                        <span className="px-2 py-0.5 text-xs font-bold uppercase rounded-full bg-green-50 text-green-700 border border-green-200">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs font-bold uppercase rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                          Inactive
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-slate-600 font-body mb-2 truncate">{sync.sheet_url}</p>
                    
                    <div className="flex items-center gap-4 text-xs text-slate-500 font-mono">
                      <span>By: {sync.user_name}</span>
                      {sync.last_sync && (
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          Last sync: {formatDate(sync.last_sync)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSync(sync.id)}
                      disabled={!sync.is_active || syncing === sync.id}
                      data-testid={`sync-now-${sync.id}`}
                      className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-body disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw size={16} className={syncing === sync.id ? 'animate-spin' : ''} />
                      <span>{syncing === sync.id ? 'Syncing...' : 'Sync Now'}</span>
                    </button>

                    <button
                      onClick={() => handleToggle(sync.id)}
                      data-testid={`toggle-sync-${sync.id}`}
                      className="p-2 text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
                      title={sync.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {sync.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                    </button>

                    <button
                      onClick={() => handleDelete(sync.id)}
                      data-testid={`delete-sync-${sync.id}`}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-slate-900 font-body mb-2">How It Works</h3>
        <ul className="text-sm text-slate-700 font-body space-y-1 ml-4 list-disc">
          <li>Add your Google Sheet URL (must be publicly accessible)</li>
          <li>Click "Sync Now" to import new leads from the sheet</li>
          <li>Duplicate phone numbers are automatically skipped</li>
          <li>Only new rows that don't exist in the database will be imported</li>
          <li>Toggle inactive to pause syncing without deleting the configuration</li>
        </ul>
      </div>
    </div>
  );
};

export default GoogleSheetSync;
